import { DEFAULT_TOUR_SETTINGS } from '../config/thresholds.js';
import { MATCHING } from '../config/timing.js';
import { winkelabstand } from './compass.js';
import { bearingDeg, distanceM, fromLocal, localFrame, toLocal, } from './distance.js';
function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
}
/** Der Kandidat mit dem geringsten Abstand zur Fahrzeugposition. */
function nächster(candidates) {
    let best = candidates[0];
    for (const candidate of candidates) {
        if (best === undefined || candidate.offTrackM < best.offTrackM) {
            best = candidate;
        }
    }
    return best;
}
/**
 * Lotet einen Punkt auf den Abschnitt zwischen zwei Spurpunkten.
 *
 * Liefert den Anteil `t` zwischen 0 und 1 und den Abstand in Metern. Ein
 * Abschnitt der Länge null — in aufgezeichneten Strecken kommen doppelte
 * Punkte durchaus vor — wird als sein Anfangspunkt behandelt.
 */
function projectOnSegment(frame, a, b, point) {
    const pa = toLocal(frame, a);
    const pb = toLocal(frame, b);
    const pp = toLocal(frame, point);
    const vx = pb.x - pa.x;
    const vy = pb.y - pa.y;
    const lengthSquared = vx * vx + vy * vy;
    const t = lengthSquared === 0 ? 0 : clamp(((pp.x - pa.x) * vx + (pp.y - pa.y) * vy) / lengthSquared, 0, 1);
    const offTrackM = Math.hypot(pp.x - (pa.x + t * vx), pp.y - (pa.y + t * vy));
    return { index: 0, t, offTrackM };
}
/**
 * Ordnet eine Fahrzeugposition der Führungsspur zu.
 *
 * `track` ist ein Spurabschnitt, nach Nummer aufsteigend. Abschnitte werden
 * nie über eine Pause hinweg zusammengefasst — sonst würde die Spur des
 * Vortages die Zuordnung verfälschen.
 *
 * Liefert `null`, wenn die Spur leer ist.
 */
/**
 * Ist die Spur ein Ring — endet sie **genau** dort, wo sie anfängt?
 *
 * Genau und nicht ungefähr: `closeGap` in der Planung hängt den Anfangspunkt
 * selbst an, der Abstand ist danach null (A-131). Eine aufgezeichnete
 * Führungsspur schließt sich dagegen nie exakt — ihre Enden lagen am
 * 16.09.2026 sechzehn Meter auseinander. Das ist der Unterschied, auf den es
 * ankommt: Eine **geplante** Runde ist ein Ring, eine **gefahrene** Spur ist
 * eine Linie, die zufällig in ihrer Nähe endet.
 *
 * Eine Schwelle dazwischen wäre eine erfundene Zahl — und sie würde jede Spur,
 * die am Ausgangspunkt endet, zum Ring erklären.
 */
function istRing(track) {
    const erster = track[0];
    const letzter = track[track.length - 1];
    return (track.length >= 2 &&
        erster !== undefined &&
        letzter !== undefined &&
        letzter.distanceM > 0 &&
        distanceM(erster, letzter) === 0);
}
/**
 * Wie weit ein Kilometerstand **vom Anker aus** liegt — vorwärts positiv.
 *
 * Auf einer Linie ist das eine Subtraktion. Auf einem **Ring** nicht: Dort
 * liegen der letzte und der erste Kilometer nebeneinander, und wer über die
 * Naht fährt, springt in der Subtraktion um die ganze Rundenlänge.
 *
 * ## Der Fall, für den es das gibt
 *
 * Am 16.09.2026 auf der Ausfahrt 450116. Die geplante Runde war seit demselben
 * Tag exakt geschlossen; beim Losfahren steht ein Fahrzeug genau auf der Naht,
 * und dort sind Kilometer 0 und Kilometer 19 367 derselbe Ort. Die Zuordnung
 * entschied sich für das Ende — und kam nicht mehr weg: Das Fenster reichte von
 * dort nur ein paar hundert Meter, die Wahrheit lag scheinbar neunzehn
 * Kilometer rückwärts, und die Notbremse greift ausdrücklich nur vorwärts.
 *
 * **Fünfzehn Minuten lang** meldete die Zuordnung bis zu 4931 m „daneben",
 * während das Fahrzeug 3 bis 9 m von der Linie entfernt fuhr. Auf dem Gerät
 * stand deshalb die Luftlinie statt der Entfernung entlang der Strecke.
 *
 * Mit dem Versatz um die Runde herum liegt derselbe Kilometerstand 357 Meter
 * **vorwärts** — der Anker wandert mit, und die Notbremse wird gar nicht erst
 * gebraucht.
 */
function versatzZumAnker(track, distanceM_, ankerM) {
    const versatz = distanceM_ - ankerM;
    if (!istRing(track)) {
        return versatz;
    }
    const laenge = track[track.length - 1]?.distanceM ?? 0;
    if (laenge <= 0) {
        return versatz;
    }
    // Auf die kürzere Seite des Rings normieren: Ein Kandidat kurz hinter der
    // Naht liegt vorwärts, nicht eine ganze Runde rückwärts.
    if (versatz > laenge / 2) {
        return versatz - laenge;
    }
    if (versatz < -laenge / 2) {
        return versatz + laenge;
    }
    return versatz;
}
/**
 * Der Punkt der Spur nach der angegebenen Streckenlänge.
 *
 * Die Umkehrung von `distanceAlong`: Dort wird zu einem Punkt die Strecke
 * gesucht, hier zu einer Strecke der Punkt. Gebraucht wird sie, um an einem
 * Anker festzuhalten, den die Kandidatenauswahl nicht mehr hergibt.
 */
function punktBeiStrecke(track, distanceM) {
    if (track.length === 0) {
        return null;
    }
    for (let i = 0; i < track.length - 1; i += 1) {
        const a = track[i];
        const b = track[i + 1];
        if (a === undefined || b === undefined) {
            continue;
        }
        if (distanceM < a.distanceM || distanceM > b.distanceM) {
            continue;
        }
        const spanne = b.distanceM - a.distanceM;
        const t = spanne <= 0 ? 0 : (distanceM - a.distanceM) / spanne;
        return {
            lat: a.lat + (b.lat - a.lat) * t,
            lon: a.lon + (b.lon - a.lon) * t,
            distanceM,
            travelMs: Math.round(a.travelMs + (b.travelMs - a.travelMs) * t),
            recordedAt: Math.round(a.recordedAt + (b.recordedAt - a.recordedAt) * t),
            index: i,
        };
    }
    // Außerhalb der Spur: das nähere Ende.
    const erster = track[0];
    const letzter = track[track.length - 1];
    if (erster === undefined || letzter === undefined) {
        return null;
    }
    const ende = distanceM <= erster.distanceM ? erster : letzter;
    return {
        lat: ende.lat,
        lon: ende.lon,
        distanceM: ende.distanceM,
        travelMs: ende.travelMs,
        recordedAt: ende.recordedAt,
        index: ende === erster ? 0 : track.length - 1,
    };
}
export function matchToTrack(track, point, options) {
    if (track.length === 0) {
        return null;
    }
    const frame = localFrame(point);
    const first = track[0];
    if (first === undefined) {
        return null;
    }
    if (track.length === 1) {
        const local = toLocal(frame, first);
        return {
            distanceM: first.distanceM,
            offTrackM: Math.hypot(local.x, local.y),
            passedAt: first.recordedAt,
            travelMs: first.travelMs,
            lat: first.lat,
            lon: first.lon,
            index: 0,
            withinHint: true,
        };
    }
    /**
     * Der Treffer, der beim bisherigen Anker bleibt.
     *
     * Er wird aus der Spur hergestellt, nicht aus den Kandidaten: Dass hier
     * niemand in der Nähe liegt, ist ja gerade der Grund, warum er gebraucht wird.
     */
    const ankerTreffer = (distanceM) => {
        const anker = punktBeiStrecke(track, distanceM);
        if (anker === null) {
            return null;
        }
        const local = toLocal(frame, anker);
        return {
            distanceM: anker.distanceM,
            offTrackM: Math.hypot(local.x, local.y),
            passedAt: anker.recordedAt,
            travelMs: anker.travelMs,
            lat: anker.lat,
            lon: anker.lon,
            index: anker.index,
            withinHint: true,
        };
    };
    // Abstand je Abschnitt.
    const candidates = [];
    for (let i = 0; i < track.length - 1; i += 1) {
        const a = track[i];
        const b = track[i + 1];
        if (a === undefined || b === undefined) {
            continue;
        }
        const projected = projectOnSegment(frame, a, b, point);
        candidates.push({ index: i, t: projected.t, offTrackM: projected.offTrackM });
    }
    if (candidates.length === 0) {
        return null;
    }
    // Nur die örtlichen Tiefpunkte kommen infrage. Jeder von ihnen ist eine
    // eigene Vorbeifahrt an dieser Stelle — bei einem Rundkurs gibt es mehrere.
    const minima = candidates.filter((candidate, i) => {
        const previous = candidates[i - 1];
        const next = candidates[i + 1];
        return ((previous === undefined || candidate.offTrackM <= previous.offTrackM) &&
            (next === undefined || candidate.offTrackM <= next.offTrackM));
    });
    const pool = richtungsgerecht(track, minima.length > 0 ? minima : candidates, options);
    // Auswahl nach dem bisherigen Verlauf.
    const hint = options.hint;
    let withinHint = true;
    let chosen = pool;
    if (hint !== undefined) {
        const maxSpeedMps = options.maxSpeedMps ?? DEFAULT_TOUR_SETTINGS.maxPlausibleSpeedMps;
        const forwardSlackM = options.forwardSlackM ?? MATCHING.forwardSlackM;
        const backwardSlackM = options.backwardSlackM ?? MATCHING.backwardSlackM;
        const elapsedS = Math.max(0, (options.at - hint.at) / 1000);
        const reach = elapsedS * maxSpeedMps;
        const vorwaerts = reach + forwardSlackM;
        const rueckwaerts = reach + backwardSlackM;
        const plausible = pool.filter((candidate) => {
            const distance = distanceAlong(track, candidate);
            if (distance === null) {
                return false;
            }
            const versatz = versatzZumAnker(track, distance, hint.distanceM);
            return versatz >= -rueckwaerts && versatz <= vorwaerts;
        });
        if (plausible.length > 0) {
            chosen = plausible;
        }
        else {
            /*
             * Kein Treffer im plausiblen Bereich. Das ist kein Fehler, sondern eine
             * Aussage: nach einem Funkloch oder einem Positionssprung ist der
             * Verlauf keine Hilfe mehr.
             *
             * Neu ansetzen darf die Zuordnung dann aber nur, wenn der neue Punkt
             * **beim Fahrzeug** liegt. Sonst entsteht aus Geometrie eine Behauptung:
             * Auf der Ausfahrt „Atteln" am 24.08.2026 wurde ein Fahrzeug, das die
             * Strecke verlassen hatte, einem Punkt 222 Meter nach dem Start
             * zugeordnet — **3826 Meter von ihm entfernt**. Aus dem Rückstand von
             * einer Minute wurden schlagartig 14:28, und der Balken beim
             * Führungsfahrzeug behauptete etwas, das es nicht gab.
             *
             * Wer die Strecke verlässt und weiterfährt, entfernt sich zu Recht
             * kilometerweit von der Stelle, an der er abgebogen ist. Genau dort
             * gehört er hin: Der Anker bleibt, wo er war, und der Rückstand wächst
             * von dort — das ist die zeitliche Fortschreibung aus Abschnitt 10 des
             * Auftrags. Erst wenn er wieder **auf** der Spur auftaucht, wird neu
             * angesetzt.
             */
            const reanchorWithinM = options.reanchorWithinM ?? DEFAULT_TOUR_SETTINGS.offRouteDistanceM;
            const freierTreffer = nächster(pool);
            if (freierTreffer !== undefined && freierTreffer.offTrackM <= reanchorWithinM) {
                withinHint = false;
            }
            else {
                /*
                 * Beim Anker bleiben — und zwar bei **dem** aus dem Verlauf, nicht bei
                 * dem, was von den Kandidaten übrig ist.
                 *
                 * Aus der Auswahl lässt er sich nicht nehmen: Dass das Fenster leer
                 * ist, heißt gerade, dass dort nichts in der Nähe liegt. Der Anker wird
                 * deshalb aus der Spur hergestellt, an der Stelle, die der Verlauf
                 * nennt.
                 */
                const gehalten = ankerTreffer(hint.distanceM);
                if (gehalten !== null) {
                    return gehalten;
                }
                withinHint = false;
            }
        }
    }
    let best = nächster(chosen);
    if (best === undefined) {
        return null;
    }
    /*
     * Das Fenster bevorzugt, es erzwingt nicht.
     *
     * Bleibt im Fenster ein Kandidat übrig, der viel weiter weg liegt als der
     * nächstgelegene überhaupt, war nicht das Fahrzeug abseits der Strecke,
     * sondern das Fenster falsch. Ohne diese Notbremse hielt es die Zuordnung
     * fest: Der Ausweg unten greift nur, wenn **gar kein** Kandidat im Fenster
     * liegt — und genau das war nicht der Fall.
     *
     * Der Vorgang vom 16.08.2026 bei Ouddorp: Beim Wenden geriet die Zuordnung
     * für einen Augenblick auf die Hinfahrt derselben Straße. Von da an lag der
     * richtige Punkt der Rückfahrt außerhalb des Fensters, der falsche darin —
     * das Fahrzeug fuhr ihm davon, und bei 150 m meldete der Server „Abseits der
     * Strecke".
     */
    const nahester = nächster(pool);
    const nahesterEntlang = nahester === undefined ? null : distanceAlong(track, nahester);
    if (hint !== undefined &&
        nahester !== undefined &&
        nahesterEntlang !== null &&
        best.offTrackM > nahester.offTrackM + MATCHING.hintOverrideM &&
        /*
         * Und nur vorwärts.
         *
         * Der Vorgang vom 20.08.2026, ebenfalls bei Ouddorp: Die Runde führte hin
         * und zurück über dieselben Straßen, Anfang und Ende der Spur lagen 84 m
         * auseinander. Am Ende parkte ein Fahrzeug drei Meter neben dem
         * **Anfang** — und die Notbremse sprang zwanzig Kilometer zurück auf die
         * Hinfahrt. Gemeldet wurden 25:09 Rückstand, während die Führung 81 Meter
         * entfernt stand.
         *
         * Rückwärts ist die Notbremse nie im Recht: Wer wirklich zurückfährt,
         * bleibt im Fenster — dafür gibt es den Spielraum entgegen der
         * Fahrtrichtung (`backwardSlackM`). Vorwärts bleibt sie unverändert, und
         * damit auch der Fall vom 16.08., für den es sie gibt.
         */
        versatzZumAnker(track, nahesterEntlang, hint.distanceM) >= 0) {
        best = nahester;
        withinHint = false;
    }
    /*
     * Vorrücken darf die Zuordnung nur, solange das Fahrzeug auf der Spur ist.
     *
     * Das Fenster aus `maxPlausibleSpeedMps` ist großzügig, und das muss es sein:
     * Ein einzelner Ausreißer darf die Zuordnung nicht verlieren. Bei 60 m/s und
     * 150 m Spielraum sind das je Sekundentakt 210 Meter. Wer abseits
     * weiterfährt, findet darin fortwährend irgendeinen Kandidaten — und die
     * Zuordnung rutscht Takt für Takt vorwärts, bis sie am Kopf der Spur steht.
     *
     * Auf der Ausfahrt „Atteln_20260825" am 25.08.2026 geschah genau das: In
     * neunundzwanzig Sekunden rückte die Zuordnung eines abgebogenen Fahrzeugs um
     * 553 Meter vor, während es sich von der Spur entfernte. Am Kopf angekommen,
     * hielt `planRejoin` es für angekommen — und die Rückführung entlang der
     * gefahrenen Spur fiel für achtzig Sekunden aus, mitten in ihrem einzigen
     * Anwendungsfall.
     *
     * Der Ausweg für das leere Fenster sagt die Regel bereits: Neu angesetzt wird
     * erst, wenn das Fahrzeug wieder **auf** der Spur ist. Dasselbe muss gelten,
     * wenn das Fenster zwar Kandidaten enthält, aber keinen beim Fahrzeug.
     */
    if (hint !== undefined && (options.advanceOnlyOnTrack ?? true)) {
        const reanchorWithinM = options.reanchorWithinM ?? DEFAULT_TOUR_SETTINGS.offRouteDistanceM;
        if (best.offTrackM > reanchorWithinM) {
            const gehalten = ankerTreffer(hint.distanceM);
            if (gehalten !== null) {
                return gehalten;
            }
        }
    }
    const a = track[best.index];
    const b = track[best.index + 1];
    if (a === undefined || b === undefined) {
        return null;
    }
    const localA = toLocal(frame, a);
    const localB = toLocal(frame, b);
    const snapped = fromLocal(frame, {
        x: localA.x + best.t * (localB.x - localA.x),
        y: localA.y + best.t * (localB.y - localA.y),
    });
    return {
        distanceM: a.distanceM + best.t * (b.distanceM - a.distanceM),
        offTrackM: best.offTrackM,
        passedAt: Math.round(a.recordedAt + best.t * (b.recordedAt - a.recordedAt)),
        travelMs: Math.round(a.travelMs + best.t * (b.travelMs - a.travelMs)),
        lat: snapped.lat,
        lon: snapped.lon,
        index: best.index,
        withinHint,
    };
}
/**
 * Wirft die Vorbeifahrten hinaus, die in die Gegenrichtung gingen.
 *
 * Führt die Strecke über eine Stichstraße, liegen Hin- und Rückweg wenige
 * Meter nebeneinander. Räumlich passen dann beide, und der räumlich nächste
 * ist ebenso oft der falsche — mit einem Rückstand in Höhe der Fahrzeit
 * zwischen beiden Vorbeifahrten.
 *
 * Wer die Straße andersherum befährt als die Führung damals, ist nicht dort,
 * wo sie damals war. Diese eine Angabe trennt die beiden Fälle sauber.
 *
 * Bleibt nichts übrig, gilt der ungefilterte Stand: Lieber eine Zuordnung, die
 * der Verlauf noch geraderücken kann, als gar keine.
 */
function richtungsgerecht(track, pool, options) {
    const kurs = options.headingDeg;
    const tempo = options.speedMps;
    if (kurs === null ||
        kurs === undefined ||
        tempo === null ||
        tempo === undefined ||
        tempo < MATCHING.courseMinSpeedMps) {
        return pool;
    }
    const passend = pool.filter((candidate) => {
        const a = track[candidate.index];
        const b = track[candidate.index + 1];
        if (a === undefined || b === undefined) {
            return false;
        }
        // Ein Abschnitt ohne Länge hat keine Richtung — er sagt nichts aus und
        // wird deshalb nicht wegen ihr verworfen.
        if (a.lat === b.lat && a.lon === b.lon) {
            return true;
        }
        return winkelabstand(bearingDeg(a, b), kurs) <= MATCHING.courseToleranceDeg;
    });
    if (passend.length === 0) {
        return pool;
    }
    /*
     * Der Filter wählt aus, er springt nicht.
     *
     * Wofür er da ist: Auf einer zweimal befahrenen Straße liegen Hin- und
     * Rückweg wenige Meter auseinander, und die Fahrtrichtung entscheidet, welche
     * Vorbeifahrt gemeint ist. Dort ist er unentbehrlich.
     *
     * Wer die Spur aber **rückwärts** zurückfährt, hat in seiner Nähe gar keinen
     * richtungsgerechten Kandidaten. Übrig bleiben dann nur weit entfernte, die
     * zufällig passen — und der nächstgelegene Punkt, auf dem das Fahrzeug
     * tatsächlich steht, fällt heraus.
     *
     * Am 27.08.2026 hielt die Zuordnung dadurch an einer Stelle fest, die 673
     * Meter vom wirklich nächsten Punkt entfernt lag. Gemeldet wurden 501 Meter
     * Abstand für ein Fahrzeug, das 1 bis 9 Meter neben der Spur fuhr, und daraus
     * zweiundfünfzig Sekunden „Abseits" — siehe A-68.
     *
     * Liegt der nächste richtungsgerechte Kandidat also deutlich weiter als der
     * nächste überhaupt, sagt die Richtung hier nichts mehr aus, und es
     * entscheidet wieder die Nähe.
     */
    const nächsterPassend = Math.min(...passend.map((c) => c.offTrackM));
    const nächsterÜberhaupt = Math.min(...pool.map((c) => c.offTrackM));
    return nächsterPassend > nächsterÜberhaupt + MATCHING.courseOverrideM ? pool : passend;
}
function distanceAlong(track, candidate) {
    const a = track[candidate.index];
    const b = track[candidate.index + 1];
    if (a === undefined || b === undefined) {
        return null;
    }
    return a.distanceM + candidate.t * (b.distanceM - a.distanceM);
}
/** Der aktuelle Stand der Führung: der letzte Punkt ihrer Spur. */
export function trackHead(track) {
    return track.length === 0 ? null : (track[track.length - 1] ?? null);
}
//# sourceMappingURL=track.js.map